import { ResourceType } from '../types/common';
import type { ResourceOption } from '../types/roles';
import { SYSADMIN_PREFIX } from './common';

export interface PermissionResourceDefinition extends ResourceOption {
    entityName?: string | null;
}

interface PermissionAppDefinition extends ResourceOption {
    category: 'core' | 'addon' | 'system';
    resources: PermissionResourceDefinition[];
}

export const WILDCARD_OPTION = { value: '*', text: 'wildcard.option' };

export const ACTION_OPTIONS = [
    { value: '*', text: 'wildcard.option' },
    { value: 'READ', text: 'action.read' },
    { value: 'RUN', text: 'action.run' },
    { value: 'WRITE', text: 'action.write' },
    { value: 'DEPLOY', text: 'action.deploy' },
    { value: 'DENY', text: 'action.deny' }
];

export const FOUNDATION_RESOURCES: PermissionResourceDefinition[] = [
    { value: ResourceType.APICLIENT, text: 'foundation.resource.apiclient', entityName: 'ApiClient' },
    { value: ResourceType.CONNECTION, text: 'foundation.resource.connection', entityName: 'Connection' },
    { value: ResourceType.DASHBOARD, text: 'foundation.resource.dashboard', entityName: 'Dashboard' },
    { value: ResourceType.ENVIRONMENT, text: 'foundation.resource.environment', entityName: 'Environment' },
    { value: ResourceType.USER, text: 'foundation.resource.user', entityName: 'User' },
    { value: ResourceType.WORKFLOW, text: 'foundation.resource.workflow', entityName: 'Workflow' },
    { value: ResourceType.GROUP, text: 'foundation.resource.group', entityName: 'Group' },
    { value: ResourceType.VARIABLE, text: 'foundation.resource.variable', entityName: 'Variable' },
    { value: ResourceType.WEBHOOK, text: 'foundation.resource.webhook', entityName: 'Webhook' },
    { value: ResourceType.METADATA, text: 'foundation.resource.metadata', entityName: null },
    { value: ResourceType.ROLE, text: 'foundation.resource.role', entityName: null },
    { value: ResourceType.GATEWAY, text: 'foundation.resource.gateway', entityName: 'Gateway' }
];

export const CUSTOM_TABLE_RESOURCES: PermissionResourceDefinition[] = [
    { value: ResourceType.META, text: 'customTable.resource.configuration' },
    { value: ResourceType.DATA, text: 'customTable.resource.data' }
];

export const AI_APPS_RESOURCES: PermissionResourceDefinition[] = [
    { value: ResourceType.APP, text: 'ai-apps.resource.app' },
    { value: ResourceType.MCP_SERVER, text: 'ai-apps.resource.mcpServer' },
    { value: ResourceType.TENANT_PROMPT, text: 'ai-apps.resource.tenantPrompt' },
    { value: ResourceType.MCP_TOOL_PERMISSION, text: 'ai-apps.resource.mcpToolPermission' }
];

export const AI_WORKFLOWS_RESOURCES: PermissionResourceDefinition[] = [
    { value: ResourceType.AI_WORKFLOW, text: 'addon.resource.aiWorkflow' }
];

export const TENANT_RESOURCES: PermissionResourceDefinition[] = [
    { value: ResourceType.TENANT, text: 'tenants.resource.tenant' }
];

const wildcardResources = [WILDCARD_OPTION];

export const ALL_APPS: PermissionAppDefinition[] = [
    { value: 'customtables', text: 'core.app.customtables', category: 'core', resources: CUSTOM_TABLE_RESOURCES },
    {
        value: 'fnd',
        text: 'core.app.fnd',
        category: 'core',
        resources: [...FOUNDATION_RESOURCES].sort((a, b) => a.value.localeCompare(b.value))
    },
    { value: 'ai-apps', text: 'addon.app.aiApps', category: 'addon', resources: AI_APPS_RESOURCES },
    { value: 'ai-workflows', text: 'addon.app.aiWorkflows', category: 'addon', resources: AI_WORKFLOWS_RESOURCES },
    { value: 'kanban', text: 'addon.app.kanban', category: 'addon', resources: wildcardResources },
    { value: 'campaign', text: 'addon.app.campaign', category: 'addon', resources: wildcardResources },
    { value: 'impex-helper', text: 'addon.app.impexHelper', category: 'addon', resources: wildcardResources },
    { value: 'mdbreadmi-helper', text: 'addon.app.mdbreadmiHelper', category: 'addon', resources: wildcardResources },
    { value: 'mi-test', text: 'addon.app.miTest', category: 'addon', resources: wildcardResources },
    { value: 'm3-printout-config', text: 'addon.app.m3PrintoutConfig', category: 'addon', resources: wildcardResources },
    { value: 'vince-pricer', text: 'addon.app.vincePricer', category: 'addon', resources: wildcardResources },
    { value: 'cugex-helper', text: 'addon.app.cugexHelper', category: 'addon', resources: wildcardResources },
    { value: 'isLantmannen', text: 'addon.app.isLantmannen', category: 'addon', resources: wildcardResources },
    { value: 'spykar', text: 'addon.app.spykar', category: 'addon', resources: wildcardResources },
    { value: 'eshopbox', text: 'addon.app.eshopbox', category: 'addon', resources: wildcardResources },
    { value: 'scanner-pick-pack', text: 'addon.app.scannerPickPack', category: 'addon', resources: wildcardResources },
    { value: 'pick-pack', text: 'addon.app.pickPack', category: 'addon', resources: wildcardResources },
    { value: 'contract-management', text: 'addon.app.contractManagement', category: 'addon', resources: wildcardResources },
    {
        value: 'sysadmin',
        text: 'core.app.tenantManagement',
        category: 'system',
        resources: TENANT_RESOURCES
    }
];

export const CORE_APPS = ALL_APPS
    .filter(app => app.category === 'core')
    .map(({ value, text }) => ({ value, text }));

export const ADDON_APPS = ALL_APPS
    .filter(app => app.category === 'addon')
    .map(({ value, text }) => ({ value, text }));

export const getAvailableApps = (
    tenantId = '',
    hasAppPermission: (app: string) => boolean = () => true
): ResourceOption[] => {
    const isSysAdmin = tenantId.startsWith(SYSADMIN_PREFIX);

    return ALL_APPS
        .filter(app => !app.value.includes('ai-workflows')) // temporary removed until ai workflows is released
        .filter(app => isSysAdmin || app.category === 'core' || hasAppPermission(app.value))
        .sort((a, b) => {
            const categoryOrder = { core: 0, system: 1, addon: 2 };
            return categoryOrder[a.category] - categoryOrder[b.category];
        })
        .map(({ value, text }) => ({ value, text }));
};

export const getAppResources = (app: string): ResourceOption[] =>
    ALL_APPS.find(definition => definition.value === app)?.resources ?? wildcardResources;
